import { message, Spin, Table } from "antd"
import React, { useEffect, useState } from "react"
import { useParams, useLocation, useNavigate } from "react-router-dom"
import CRMService from "src/services/CRMService"
import "../styles/DepartmentPerfomanceDetail.css"

// ---------- helpers ----------
const fmt = (val) => Number(val || 0).toLocaleString("vi-VN")
const pct = (val) => `${Math.round(val || 0)}%`

const DonutChart = ({ percent = 0, color = "#378add", size = 72, stroke = 8 }) => {
    const r = (size - stroke) / 2
    const circ = 2 * Math.PI * r
    const filled = (percent / 100) * circ
    const cx = size / 2
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--color-border-tertiary, #e5e7eb)" strokeWidth={stroke} />
            <circle
                cx={cx} cy={cx} r={r} fill="none"
                stroke={color} strokeWidth={stroke}
                strokeDasharray={`${filled} ${circ - filled}`}
                strokeLinecap="round"
            />
        </svg>
    )
}

const StatCard = ({ label, value, subtext, subtextColor, highlight }) => (
    <div className={`dpd-stat-card${highlight ? " dpd-stat-card--highlight" : ""}`}>
        <div className="dpd-stat-val">{value}</div>
        <div className="dpd-stat-lbl">{label}</div>
        {subtext && (
            <div className="dpd-stat-sub" style={{ color: subtextColor }}>
                {subtext}
            </div>
        )}
    </div>
)

const RateCard = ({ label, percent, color, count }) => (
    <div className="dpd-rate-card">
        <div className="dpd-donut-wrap">
            <DonutChart percent={percent} color={color} />
            <div className="dpd-donut-center">{pct(percent)}</div>
        </div>
        <div className="dpd-rate-name">{label}</div>
        {count != null && <div className="dpd-rate-count">{fmt(count)} đơn</div>}
    </div>
)

// ---------- columns ----------
const buildColumns = (maxMoney) => [
    {
        title: "Nhân viên",
        dataIndex: "personnel_name",
        render: (name) => {
            const initials = name
                ? name.split(" ").slice(-2).map((w) => w[0]).join("")
                : "?"
            return (
                <div className="dpd-nv-cell">
                    <div className="dpd-avatar">{initials}</div>
                    <span className="dpd-nv-name">{name}</span>
                </div>
            )
        },
    },
    {
        title: "Tổng đơn",
        dataIndex: "total_orders",
        align: "center",
        sorter: (a, b) => a.total_orders - b.total_orders,
    },
    {
        title: "Thành công",
        dataIndex: "success_orders",
        align: "center",
        sorter: (a, b) => a.success_orders - b.success_orders,
        render: (v) => <span className="dpd-pill dpd-pill--success">{v}</span>,
    },
    {
        title: "Hoàn đơn",
        dataIndex: "refund_orders",
        align: "center",
        sorter: (a, b) => a.refund_orders - b.refund_orders,
        render: (v) => <span className="dpd-pill dpd-pill--refund">{v}</span>,
    },
    {
        title: "Đơn chờ",
        dataIndex: "pending_orders",
        align: "center",
        render: (v) => <span className="dpd-pill dpd-pill--pending">{v}</span>,
    },
    {
        title: "Đơn đang trả",
        dataIndex: "returning_orders",
        align: "center",
        render: (v) => <span className="dpd-pill dpd-pill--returning">{v}</span>,
    },
    {
        title: "Tỷ lệ TC",
        key: "rate",
        align: "center",
        sorter: (a, b) =>
            (a.success_orders / a.total_orders || 0) - (b.success_orders / b.total_orders || 0),
        render: (_, row) => {
            const r = row.total_orders ? Math.round((row.success_orders / row.total_orders) * 100) : 0
            return (
                <div className="dpd-rate-cell">
                    <span className="dpd-rate-num">{r}%</span>
                    <div className="dpd-bar-bg">
                        <div className="dpd-bar-fill" style={{ width: `${r}%` }} />
                    </div>
                </div>
            )
        },
    },
    {
        title: "Doanh số",
        dataIndex: "total_money",
        align: "right",
        sorter: (a, b) => a.total_money - b.total_money,
        render: (v, _, idx) => {
            const barW = maxMoney ? Math.round((v / maxMoney) * 100) : 0
            return (
                <div className="dpd-money-cell">
                    <span>{fmt(v)} đ</span>
                    <div className="dpd-money-bar-bg">
                        <div className="dpd-money-bar-fill" style={{ width: `${barW}%` }} />
                    </div>
                </div>
            )
        },
    },
]

// ---------- component ----------
const DepartmentPerfomanceDetail = () => {
    const { id } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [dataDetail, setDataDetail] = useState({})

    const filter = location.state?.filter

    const fetchDataDetail = async () => {
        console.log(filter);
        try {
            setLoading(true)
            const res = await CRMService.detailDepartmentOrder(id, { filter })
            setDataDetail(res.success)
        } catch (error) {
            console.log(error)
            message.error("Lấy dữ liệu không thành công!")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchDataDetail()
    }, [id])

    const overall = dataDetail?.overall || {}
    const rates = overall?.rates || {}
    const sales = dataDetail?.sales || []
    const maxMoney = sales.length ? Math.max(...sales.map((s) => Number(s.total_money || 0))) : 1

    const rateItems = [
        { label: "Thành công", key: "success_rate", color: "#1a7f4b", countKey: "success_orders" },
        { label: "Chờ xử lý", key: "pending_rate", color: "#ba7517", countKey: "pending_orders" },
        { label: "Đơn hoàn", key: "refund_rate", color: "#c0392b", countKey: "refund_orders" },
        { label: "Đơn đang giữ", key: "hold_rate", color: "#534ab7", countKey: "hold_orders" },
        { label: "Đơn đang trả", key: "returning_rate", color: "#534ab7", countKey: "returning_orders" },
    ]

    return (
        <Spin spinning={loading}>
            <div className="dpd-wrap">
                {/* Header */}
                <div className="dpd-header">
                    <button className="dpd-back-btn" onClick={() => navigate(-1)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        Quay lại
                    </button>
                    <div>
                        <h1 className="dpd-title">Chi tiết hiệu suất phòng ban</h1>
                        {filter && (
                            <div className="dpd-subtitle">
                                {filter.from && filter.to
                                    ? `${filter.from} – ${filter.to}`
                                    : "Toàn bộ thời gian"}
                            </div>
                        )}
                    </div>
                </div>

                {/* Stat cards */}
                <div>
                    <div className="dpd-section-label">Tổng quan</div>
                    <div className="dpd-stats-grid">
                        <StatCard
                            label="Tổng đơn"
                            value={fmt(overall.total_orders)}
                        />
                        <StatCard
                            label="Đơn chờ"
                            value={fmt(overall.pending_orders)}
                            subtext={
                                overall.total_orders
                                    ? `${Math.round((overall.pending_orders / overall.total_orders) * 100)}% tổng đơn`
                                    : null
                            }
                            subtextColor="var(--color-text-tertiary)"
                        />
                        <StatCard
                            label="Thành công"
                            value={fmt(overall.success_orders)}
                            subtext={
                                overall.total_orders
                                    ? `${Math.round((overall.success_orders / overall.total_orders) * 100)}% tổng đơn`
                                    : null
                            }
                            subtextColor="#1a7f4b"
                        />
                        <StatCard
                            label="Hoàn đơn"
                            value={fmt(overall.refund_orders)}
                            subtext={
                                overall.total_orders
                                    ? `${Math.round((overall.refund_orders / overall.total_orders) * 100)}% tổng đơn`
                                    : null
                            }
                            subtextColor="#c0392b"
                        />
                        <StatCard
                            label="Giữ đơn"
                            value={fmt(overall.hold_orders)}
                        />
                        <StatCard
                            label="Trong quá trình trả đơn"
                            value={fmt(overall.hold_orders)}
                        />

                    </div>
                    <div className="dpd-section-label"></div>
                    <div className="dpd-stats-grid">
                        <StatCard
                            label="Doanh số"
                            value={`${fmt(overall.total_money)} đ`}
                            highlight
                        />
                        <StatCard
                            label="Doanh thu đơn chờ"
                            value={`${fmt(overall.pending_money)} đ`}
                            highlight
                        />
                        <StatCard
                            label="Doanh thu thành công"
                            value={`${fmt(overall.success_money)} đ`}
                            highlight
                        />
                        <StatCard
                            label="Doanh thu đơn hoàn"
                            value={`${fmt(overall.refund_money)} đ`}
                            highlight
                        />

                        <StatCard
                            label="Doanh thu đơn đang giữ"
                            value={`${fmt(overall.hold_money)} đ`}
                            highlight
                        />

                        <StatCard
                            label="Doanh thu đơn đang trả"
                            value={`${fmt(overall.returning_money)} đ`}
                            highlight
                        />
                    </div>
                </div>

                {/* Rate circles */}
                <div>
                    <div className="dpd-section-label">Tỷ lệ đơn hàng</div>
                    <div className="dpd-rates-row">
                        {rateItems.map((item) => (
                            <RateCard
                                key={item.key}
                                label={item.label}
                                percent={rates[item.key] || 0}
                                color={item.color}
                                count={overall[item.countKey]}
                            />
                        ))}
                    </div>
                </div>

                {/* Staff table */}
                <div className="dpd-table-card">
                    <div className="dpd-table-head">
                        <h3>Danh sách nhân viên</h3>
                        {sales.length > 0 && (
                            <span className="dpd-badge">{sales.length} thành viên</span>
                        )}
                    </div>
                    <Table
                        rowKey="id"
                        columns={buildColumns(maxMoney)}
                        dataSource={sales}
                        pagination={false}
                        size="middle"
                        className="dpd-table"
                    />
                </div>
            </div>
        </Spin>
    )
}

export default DepartmentPerfomanceDetail